import { useState, useEffect, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import bigLogo from "@/assets/big-logo.png";
import { Copy, Check, ExternalLink } from "lucide-react";
import { buildStudioLoginUrl } from "@/lib/utils";
import type { TosConfig } from "@/types/firestore";

// ─── Types ───────────────────────────────────────────────────────────────────

interface LinkInfo {
  status: string;
  scope: string;
  studioName: string;
  orgName: string | null;
  orgLogo: string | null;
  tierName: string | null;
  groupName: string | null;
  targetEmail: string | null;
  targetName: string | null;
  productNames: string[] | null;
  tos: TosConfig;
}

interface RedeemResult {
  email: string;
  name: string;
  temporaryPassword: string;
  studioName: string;
  studioLoginUrl: string | null;
  tierName: string | null;
  groupName: string | null;
}

type PageState =
  | { status: "loading" }
  | { status: "invalid"; message: string }
  | { status: "ready"; info: LinkInfo }
  | { status: "processing"; info: LinkInfo }
  | { status: "success"; result: RedeemResult }
  | { status: "error"; message: string; info: LinkInfo };

// ─── Cloud Function Refs ────────────────────────────────────────────────────

const getInviteLinkInfoFn = httpsCallable<
  { token: string },
  { success: boolean; data?: LinkInfo; error?: string }
>(functions, "getInviteLinkInfo");

const redeemInviteLinkFn = httpsCallable<
  { token: string; email: string; name?: string; tosAccepted?: boolean },
  { success: boolean; data?: RedeemResult; error?: string }
>(functions, "redeemInviteLink");

// ─── Component ───────────────────────────────────────────────────────────────

export function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>({ status: "loading" });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [tosChecked, setTosChecked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [orgLogo, setOrgLogo] = useState<string | null | undefined>(undefined);

  // Load invite link info on mount
  useEffect(() => {
    if (!token) {
      setPageState({ status: "invalid", message: "No invite link provided." });
      return;
    }

    async function loadLinkInfo() {
      try {
        const result = await getInviteLinkInfoFn({ token: token! });

        if (!result.data.success || !result.data.data) {
          setOrgLogo(null);
          setPageState({
            status: "invalid",
            message: result.data.error || "This invite link is not valid.",
          });
          return;
        }

        const info = result.data.data;

        if (info.status !== "active") {
          const messages: Record<string, string> = {
            expired: "This invite link has expired.",
            exhausted: "This invite link has reached its usage limit.",
            revoked: "This invite link is no longer active.",
          };
          setOrgLogo(info.orgLogo);
          setPageState({
            status: "invalid",
            message:
              messages[info.status] || "This invite link is no longer valid.",
          });
          return;
        }

        // Store org logo for all page states (null = no org logo, string = has logo)
        setOrgLogo(info.orgLogo);

        // Pre-fill email for individual scope
        if (info.scope === "individual" && info.targetEmail) {
          setEmail(info.targetEmail);
        }
        if (info.scope === "individual" && info.targetName) {
          setName(info.targetName);
        }

        setPageState({ status: "ready", info });
      } catch (err) {
        console.error("Error loading invite link:", err);
        setOrgLogo(null);
        setPageState({
          status: "invalid",
          message: "Unable to load invite link. Please try again.",
        });
      }
    }

    loadLinkInfo();
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    const info =
      pageState.status === "ready"
        ? pageState.info
        : pageState.status === "error"
          ? pageState.info
          : null;
    if (!info) return;

    setPageState({ status: "processing", info });

    try {
      const result = await redeemInviteLinkFn({
        token: token!,
        email: email.trim().toLowerCase(),
        name: name.trim() || undefined,
        tosAccepted: info.tos?.required ? tosChecked : undefined,
      });

      if (result.data.success && result.data.data) {
        setPageState({ status: "success", result: result.data.data });
      } else if (result.data.error === "TOS_REQUIRED") {
        setTosChecked(false);
        setPageState({
          status: "error",
          message: `You must accept the ${info.tos?.label || "Terms of Service"} to continue.`,
          info,
        });
      } else {
        setPageState({
          status: "error",
          message:
            result.data.error || "Something went wrong. Please try again.",
          info,
        });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong.";

      let displayMessage = message;
      if (
        message.includes("already-exists") ||
        message.includes("already exists")
      ) {
        displayMessage =
          "An account with this email already exists. Please contact your administrator.";
      } else if (message.includes("expired")) {
        displayMessage = "This invite link has expired.";
      } else if (message.includes("no longer active")) {
        displayMessage = "This invite link is no longer active.";
      } else if (
        message.includes("usage limit") ||
        message.includes("maximum")
      ) {
        displayMessage = "This invite link has reached its usage limit.";
      } else if (message.includes("different email")) {
        displayMessage =
          "This link was generated for a different email address.";
      }

      setPageState({ status: "error", message: displayMessage, info });
    }
  }

  async function handleCopyPassword(password: string) {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.querySelector<HTMLInputElement>("#temp-password");
      if (el) {
        el.select();
        document.execCommand("copy");
      }
    }
  }

  // ─── Loading ───────────────────────────────────────────────────────────────

  if (pageState.status === "loading") {
    return (
      <Shell orgLogo={orgLogo}>
        <p className="font-body text-sm text-text-muted text-center">
          Loading invite link...
        </p>
      </Shell>
    );
  }

  // ─── Invalid ───────────────────────────────────────────────────────────────

  if (pageState.status === "invalid") {
    return (
      <Shell orgLogo={orgLogo}>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 text-4xl">:(</div>
          <h2 className="mb-2 font-heading text-lg font-semibold text-text">
            Link Unavailable
          </h2>
          <p className="font-body text-sm text-text-muted">
            {pageState.message}
          </p>
          <p className="mt-4 font-body text-sm text-text-muted">
            Please contact your administrator for a new invite link.
          </p>
        </div>
      </Shell>
    );
  }

  // ─── Processing ────────────────────────────────────────────────────────────

  if (pageState.status === "processing") {
    return (
      <Shell orgLogo={orgLogo}>
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-sidebar border-t-teal" />
          <p className="font-body text-sm text-text-muted">
            Creating your account...
          </p>
        </div>
      </Shell>
    );
  }

  // ─── Success ───────────────────────────────────────────────────────────────

  if (pageState.status === "success") {
    const { result } = pageState;
    return (
      <Shell orgLogo={orgLogo}>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 text-center text-4xl text-teal">&#10003;</div>
          <h2 className="mb-2 text-center font-heading text-lg font-semibold text-text">
            You're All Set!
          </h2>
          <p className="mb-6 text-center font-body text-sm text-text-muted">
            Your account has been created for{" "}
            <strong>{result.studioName}</strong>.
          </p>

          {/* Credentials card */}
          <div className="rounded-md bg-sidebar p-4 space-y-3">
            <div>
              <p className="font-body text-xs text-text-muted">Email</p>
              <p className="font-body text-sm font-medium text-text">
                {result.email}
              </p>
            </div>
            <div>
              <p className="font-body text-xs text-text-muted">
                Temporary Password
              </p>
              <div className="flex items-center gap-2">
                <input
                  id="temp-password"
                  type="text"
                  readOnly
                  value={result.temporaryPassword}
                  className="flex-1 rounded-md border border-border bg-white px-3 py-2 font-mono text-base font-semibold text-text tracking-wide"
                />
                <button
                  type="button"
                  onClick={() => handleCopyPassword(result.temporaryPassword)}
                  className="rounded-md border border-border bg-white px-3 py-2 text-sm text-text hover:bg-sidebar transition-colors"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-teal" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <p className="mt-4 font-body text-xs text-text-muted text-center">
            We've also emailed these credentials to {result.email}.
          </p>

          {result.studioLoginUrl ? (
            <>
              <p className="mt-4 font-body text-sm font-semibold text-orange-red text-center leading-relaxed">
                Click the button below, then select "Log In" and enter the email
                and password shown above.
              </p>
              <a
                href={buildStudioLoginUrl(result.studioLoginUrl) ?? result.studioLoginUrl}
                className="mt-4 inline-block w-full rounded-md bg-teal px-4 py-2 text-center font-heading text-sm font-semibold text-white hover:bg-teal/85 transition-colors"
              >
                Go to Studio
              </a>
              <p className="mt-2 font-body text-xs text-text-muted text-center">
                Please change your password after your first login.
              </p>
            </>
          ) : (
            <p className="mt-4 font-body text-sm text-text-muted text-center">
              Please navigate to your studio login page and sign in with the
              credentials above. Change your password after your first login.
            </p>
          )}
        </div>
      </Shell>
    );
  }

  // ─── Ready / Error (form) ─────────────────────────────────────────────────

  const info =
    pageState.status === "ready" ? pageState.info : pageState.info;
  const isIndividual = info.scope === "individual";
  const errorMessage =
    pageState.status === "error" ? pageState.message : null;

  const tierDisplay =
    info.tierName || (info.productNames ? info.productNames.join(", ") : null);

  return (
    <Shell orgLogo={orgLogo}>
      <div className="mb-8 flex flex-col items-center">
        <h1 className="font-heading text-2xl font-bold text-text">
          {isIndividual && info.targetName
            ? `Welcome, ${info.targetName}!`
            : `Join ${info.studioName}`}
        </h1>
        {info.orgName && (
          <p className="mt-1 font-body text-sm text-text-muted">
            {info.orgName}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        {/* What you'll get */}
        {(tierDisplay || info.groupName) && (
          <div className="mb-4 rounded-md bg-sidebar p-3">
            <p className="mb-1 font-body text-xs font-medium text-text-muted">
              You'll have access to:
            </p>
            <div className="space-y-1">
              {tierDisplay && (
                <p className="font-body text-sm font-medium text-text">
                  {tierDisplay}
                </p>
              )}
              {info.groupName && (
                <p className="font-body text-xs text-text-muted">
                  Group: {info.groupName}
                </p>
              )}
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 rounded-md bg-orange-red/10 p-3 text-sm text-orange-red">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="join-email"
              className="mb-1 block font-body text-sm font-medium text-text"
            >
              Email
            </label>
            <input
              id="join-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={isIndividual && !!info.targetEmail}
              className={`w-full rounded-md border border-border px-3 py-2 font-body text-sm text-text placeholder-text-muted focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal ${
                isIndividual && info.targetEmail ? "bg-sidebar" : "bg-white"
              }`}
              placeholder="you@company.com"
            />
          </div>

          {!isIndividual && (
            <div>
              <label
                htmlFor="join-name"
                className="mb-1 block font-body text-sm font-medium text-text"
              >
                Full Name{" "}
                <span className="font-normal text-text-muted">(optional)</span>
              </label>
              <input
                id="join-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-border bg-white px-3 py-2 font-body text-sm text-text placeholder-text-muted focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
                placeholder="Jane Smith"
              />
            </div>
          )}

          {info.tos?.required && (
            <div className="rounded-md border border-border bg-sidebar p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tosChecked}
                  onChange={(e) => setTosChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-teal focus:ring-teal"
                />
                <span className="font-body text-sm text-text">
                  I agree to the{" "}
                  {info.tos.fileUrl ? (
                    <a
                      href={info.tos.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-teal hover:text-teal/80"
                    >
                      {info.tos.label || "Terms of Service"}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-medium">
                      {info.tos.label || "Terms of Service"}
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={info.tos?.required && !tosChecked}
            className="w-full rounded-md bg-teal px-4 py-2 font-heading text-sm font-semibold text-white hover:bg-teal/85 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Join Now
          </button>
        </form>
      </div>
    </Shell>
  );
}

// ─── Layout Shell ────────────────────────────────────────────────────────────

function Shell({ children, orgLogo }: { children: React.ReactNode; orgLogo?: string | null }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex h-14 justify-center">
          {orgLogo === undefined ? (
            <div className="h-14 w-[200px]" />
          ) : (
            <img
              src={orgLogo || bigLogo}
              alt="Logo"
              className="h-14 max-w-[200px] object-contain"
            />
          )}
        </div>
        {children}
        <p className="mt-6 text-center font-body text-xs text-text-muted">
          Powered by BiG &mdash; bigimpactgroup.ai
        </p>
      </div>
    </div>
  );
}
